import React from 'react'
import { useStaticQuery, graphql, Link, withPrefix } from 'gatsby'
import styled from '@xstyled/styled-components'
// eslint-disable-next-line import/no-unresolved
import { useLocation } from '@reach/router'
import { unslugify } from 'unslugify'
import { v4 as uuidv4 } from 'uuid'

const SideNavQueryNew = graphql`
  query SideNavQueryNew {
    allMdx(filter: { fields: { pageType: { eq: "doc" }, title: { ne: "" } } }) {
      edges {
        node {
          id
          fields {
            title
            pageType
            section
            order
            slug
          }
        }
      }
    }

    allStrapiDocsSection(sort: { fields: order }) {
      nodes {
        section_name
      }
    }

    allStrapiTutorial {
      edges {
        node {
          id
          title
          fields {
            id
            pageType
            title
            slug
            section
            order
          }
        }
      }
    }

    strapiGlossary {
      id
      fields {
        id
        pageType
        title
        slug
        section
        order
      }
    }

    allStrapiDocsChangeLog {
      nodes {
        id
        fields {
          id
          order
          pageType
          title
          slug
          section
        }
      }
    }

    allStrapiDocsPage(filter: { name: { ne: "dummy" } }) {
      nodes {
        id
        fields {
          id
          order
          pageType
          title
          slug
          section
        }
      }
    }
    allJson(filter: { fields: { pageType: { eq: "swag" } } }) {
      distinct(field: fields___api)
    }
    home: allHtml(filter: { fields: { pageType: { eq: "docfx" }, directory: { eq: "home" } } }) {
      distinct(field: fields___api)
    }
    directories: allHtml(filter: { fields: { pageType: { eq: "docfx" } } }) {
      distinct(field: fields___directory)
    }
  }
`

const createOrFindGroup = (name, groups) => {
  const existingGroup = groups.find(group => name.toUpperCase().includes(group.name.toUpperCase()))
  if (existingGroup) return existingGroup

  const group = { name, nodes: [] }
  groups.push(group)
  return group
}

const DEFAULT_ORDER_VALUE = -9999

const sortNodes = (a, b) => {
  const diff =
    a.fields.order !== DEFAULT_ORDER_VALUE && b.fields.order !== DEFAULT_ORDER_VALUE
      ? a.fields.order - b.fields.order
      : a.fields.order !== DEFAULT_ORDER_VALUE
      ? -1
      : b.fields.order !== DEFAULT_ORDER_VALUE
      ? 1
      : a.fields.title.localeCompare(b.fields.title)
  return diff === 0 ? 0 : diff > 0 ? 1 : -1
}

const groupNodes = nodes =>
  nodes.reduce((groups, node) => {
    const group = createOrFindGroup(node.fields.section, groups)
    group.nodes.push(node)
    group.nodes.sort(sortNodes)
    return groups
  }, [])

const Nav = styled.nav`
  padding: 4 3 5;
`

const NavGroup = styled.div`
  margin-bottom: 4;
`

const NavGroupTitle = styled.h4`
  font-size: 14;
  font-weight: 500;
  color: on-background-light;
  text-transform: uppercase;
  margin: 0 0 3 0;
`

const NavGroupMenu = styled.ul`
  margin: 0;
  padding: 0;
  border-left: 1;
  border-left-color: layout-border;
`

const NavGroupMenuItem = styled.li`
  list-style-type: none;
  margin: 2 0 0 -2px;
  padding: 0;
  font-size: 14;
  font-weight: 500;

  a {
    color: on-background-light;
    display: block;
    transition: fast;
    transition-property: color, border-color;
    padding: 2px 0 2px 2;
    border-left: 3;
    border-color: transparent;
    text-decoration: none;
    opacity: 0.85;

    &:hover {
      color: on-background;
      opacity: 1;
    }

    &[aria-current='page'] {
      font-weight: 600;
      border-color: primary;
      color: on-background;
      opacity: 1;
    }
  }
`

const sortGroupsWithConfig = section_name => (a, b) => {
  const indexA = section_name.indexOf(a.name.toUpperCase())
  const indexB = section_name.indexOf(b.name.toUpperCase())
  const diff = indexA - indexB
  return diff === 0 ? 0 : diff < 0 ? -1 : 1
}

const adjustSection = (section, sections) => {
  const existingSection = sections.find(sec => section.toUpperCase().includes(sec))
  if (existingSection) return existingSection
  return 'Gameplay'
}

const buildMenuList = (source, sections, appendage) => {
  return source.map((li, idx) => {
    const titleFix = appendage ? `${unslugify(li)} - ${appendage}` : unslugify(li)
    const id = uuidv4()
    const MenuObj = {
      name: li,
      id: id,
      fields: {
        id,
        slug: `/api/${li}`,
        title: titleFix,
        section: adjustSection(li, sections),
        order: idx,
      },
    }
    return MenuObj
  })
}

const buildDocFxHeaders = data => {
  const dirs = data.directories.distinct
  const result = dirs
    .map(dir => dir)
    .reduce((obj, id) => {
      return { ...obj, [id]: data[id] && data[id].distinct }
    }, {})
  return result
}

const addRootChangelogs = changelogs => {
  // if the title contains the word Changelogs, remove it for displaying on the side nav
  const fixedTitlesChangelog = changelogs.map(changelog =>
    changelog.fields.title.includes(' Changelogs')
      ? {
          ...changelog,
          fields: { ...changelog.fields, title: changelog.fields.title.replace(' Changelogs', '') },
        }
      : changelog
  )

  const newChangelogs = [...fixedTitlesChangelog]
  newChangelogs.unshift({
    id: uuidv4(),
    name: 'changelogs',
    fields: {
      id: uuidv4(),
      slug: '/changelogs/',
      title: 'Latest',
      section: 'Changelogs',
      order: 1,
    },
  })
  return newChangelogs
}

export function useSideNavState() {
  const data = useStaticQuery(SideNavQueryNew)
  const docFxHeaders = buildDocFxHeaders(data)
  const strapi_sections = data.allStrapiDocsSection.nodes.map(node =>
    node.section_name.toUpperCase().trim()
  )
  const changelogs = addRootChangelogs(data.allStrapiDocsChangeLog.nodes)

  return React.useMemo(() => {
    const navGroups = groupNodes([
      ...data.allMdx.edges.map(edge => edge.node),
      ...data.allStrapiTutorial.edges.map(edge => edge.node),
      ...changelogs,
      ...data.allStrapiDocsPage.nodes,
      data.strapiGlossary,
      ...buildMenuList(data.allJson.distinct, strapi_sections, ''),
      ...buildMenuList(docFxHeaders.home, strapi_sections, ''),
    ])
    navGroups.sort(sortGroupsWithConfig(strapi_sections))

    return { navGroups }
  }, [data, changelogs, docFxHeaders.home, strapi_sections])
}

export function useSideNavPrevNext({ navGroups }) {
  const { pathname } = useLocation()
  const nodes = navGroups.flatMap(group => group.nodes)
  const nodeIndex = nodes.findIndex(node => withPrefix(node.fields.slug) === pathname)
  return {
    prev: nodeIndex > -1 ? nodes[nodeIndex - 1] : null,
    next: nodeIndex > -1 ? nodes[nodeIndex + 1] : null,
  }
}

export function SideNav({ navGroups }) {
  return (
    <Nav>
      {navGroups.map((navGroup, index) => (
        <NavGroup key={index}>
          <NavGroupTitle>{navGroup.name}</NavGroupTitle>
          <NavGroupMenu key={index}>
            {navGroup.nodes.map((page, idx) => (
              <NavGroupMenuItem key={idx}>
                <Link to={page.fields.slug}>{page.fields.title}</Link>
              </NavGroupMenuItem>
            ))}
          </NavGroupMenu>
        </NavGroup>
      ))}
    </Nav>
  )
}
